import Chip from "@material-ui/core/Chip";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import React, { FC, useContext, useMemo } from "react";
import { ExpertMinumimFixedUsers, ExpertRole } from "../../data/constants";
import { ExamDayType } from "../../model/ExamDayType";
import { ExamDayViewModel } from "../../model/ExamDayViewModel";
import { ExpertViewModel } from "../../model/ExpertViewModel";
import { RoomAssignmentViewModel } from "../../model/RoomAssignmentViewModel";
import TranslatedMessage from "../translations/TranslatedMessage";
import RoomAssignmentExpertSelect, {
  RoomAssignmentExpertSelectProps,
} from "../userRoomAssignmentBoard/RoomAssignmentExpertSelect";
import { SchedulerContextValue } from "./context/SchedulerContext";
import { SchedulerExpertContextValue } from "./context/SchedulerExpertContext";

const getMinFixedExperts = (examDay: ExamDayViewModel, roomAssignment: RoomAssignmentViewModel) => {
  if (examDay.type === ExamDayType.Written) {
    return ExpertMinumimFixedUsers.proctorWrittenExam;
  } else if (roomAssignment.isPreparationRoom) {
    return ExpertMinumimFixedUsers.proctorOralPreparation;
  }
  return ExpertMinumimFixedUsers.expertOralExam;
};
interface SchedulerProctorRowProps {
  isFixed: boolean;
  allExperts: ExpertViewModel[] | undefined;
  showAllExperts: boolean;
  readOnly: boolean;
  isSessionPublished: boolean;
}

const SchedulerProctorRow: FC<SchedulerProctorRowProps> = ({ isFixed, allExperts, showAllExperts, readOnly }) => {
  const { selectedExamDayId, rooms, examDays, examSession } = useContext(SchedulerContextValue);
  const { changeExpert, schedulerExperts } = useContext(SchedulerExpertContextValue);

  const expertSelectors = useMemo(() => {
    return rooms
      .map((room) => {
        const examDay = examDays.find((examDay) => examDay.id === selectedExamDayId);
        const roomAssignment = examDay?.roomAssignments.find((r) => r.roomId === room.id);
        const expertRole =
          examDay?.type === ExamDayType.Written ||
          (examDay?.type === ExamDayType.Oral && roomAssignment?.isPreparationRoom)
            ? ExpertRole.Aufsichtsexperten
            : ExpertRole.Prüfungsexperten;

        if (!examDay || !roomAssignment || !examSession || !allExperts) {
          return null;
        }

        const roomAssignedWithUpdatedExperts: RoomAssignmentViewModel = {
          ...roomAssignment,
          assignedUsers:
            schedulerExperts
              .find((expert) => expert.examDayId === examDay.id)
              ?.expertAssignments.find((ea) => ea.roomId === room.id)
              ?.assignedUsers.filter((u) => u.isFixed === isFixed) || [],
        };

        if (readOnly) {
          const experts = roomAssignedWithUpdatedExperts.assignedUsers.map((assignedUser) => {
            const expert = allExperts.find((expert) => expert.id === assignedUser.userId);
            const expertName = expert !== undefined ? `${expert.firstName} ${expert.lastName}` : assignedUser.userId;
            return <Chip key={expert?.id} label={expertName} />;
          });
          return (
            <TableCell key={room.id}>
              {experts.length > 0 ? (
                experts
              ) : (
                <>
                  {"<"}
                  <TranslatedMessage id="scheduler.message.noExperts" />
                  {">"}
                </>
              )}
            </TableCell>
          );
        }

        const minimumUsers = (isFixed && getMinFixedExperts(examDay, roomAssignment)) || undefined;
        const expertSelectProps: RoomAssignmentExpertSelectProps = {
          examDay,
          examSession,
          roomAssignment: roomAssignedWithUpdatedExperts,
          isFixedExpert: isFixed,
          expertRole,
          allExperts,
          showAllExperts,
          minimumUsers,
          change: changeExpert(examDay.id, room.id),
        };

        return <TableCell key={room.id}>{<RoomAssignmentExpertSelect {...expertSelectProps} />}</TableCell>;
      })
      .filter((expertSelect) => expertSelect);
  }, [
    allExperts,
    changeExpert,
    examDays,
    examSession,
    isFixed,
    readOnly,
    rooms,
    schedulerExperts,
    selectedExamDayId,
    showAllExperts,
  ]);

  if (allExperts === undefined || examSession === undefined || examDays === undefined) {
    return <></>;
  }

  const translationId = isFixed ? "userRoomAssignment.label.fixed" : "userRoomAssignment.label.reserve";
  return (
    <TableRow>
      <TableCell key={translationId}>
        <TranslatedMessage id={translationId} />
      </TableCell>
      {expertSelectors}
    </TableRow>
  );
};

export default SchedulerProctorRow;
